import { inject, Injectable } from '@angular/core'
import { EVENT } from '@app/analytics/analytics.cnst.js'
import { AnalyticsService } from '@app/analytics/analytics.service.js'
import { B1Command, DeviceStatus, ErrorStatus, SleepLogData } from '@app/model/b1.model.js'
import { logUtil } from '@app/util/log.util.js'
import { _anyToErrorObject } from '@naturalcycles/js-lib'
import { B1 } from '@src/typings/capacitor'

@Injectable({
  providedIn: 'root',
})
export class B1PluginService {
  private analyticsService = inject(AnalyticsService)

  public async initialize(): Promise<void> {
    try {
      const { value } = await B1.initialize()
      console.warn('[dev]', '[b1] initialize', value)
    } catch (err) {
      this.logError('initialize', err)
    }
  }

  public async scan(): Promise<void> {
    try {
      await B1.scan()
    } catch (err) {
      this.logError('scan', err)
    }
  }

  public async stopScan(): Promise<void> {
    try {
      await B1.stopscan()
    } catch (err) {
      this.logError('stopscan', err)
    }
  }

  public async getDevices(): Promise<string[]> {
    try {
      const { devices } = await B1.getDevices()
      return devices
    } catch (err) {
      this.logError('getDevices', err)
      return []
    }
  }

  public async connect(deviceName: string): Promise<void> {
    try {
      await B1.connect({ toDevice: deviceName })
    } catch (err) {
      this.logError('connect', err)
    }
  }

  public async disconnect(): Promise<void> {
    try {
      await B1.disconnect()
      console.warn('[dev]', '[b1] disconnected')
    } catch (err) {
      this.logError('disconnect', err)
    }
  }

  public async isConnected(): Promise<boolean> {
    try {
      const { Connected: connected } = await B1.getIsConnected()
      console.warn('[dev]', '[b1] isConnected', connected)
      return connected
    } catch (err) {
      this.logError('isConnected', err)
      return false
    }
  }

  public async getDeviceStatus(name?: string): Promise<DeviceStatus | undefined> {
    try {
      const {
        BatteryState: batteryState,
        BatteryStateString: batteryStateString,
        DeviceMode: deviceMode,
        DeviceModeString: deviceModeString,
        Timestamp: timestamp,
        LogsToRead: logsToRead,
        State: state,
        StateString: stateString,
        BatteryVoltage: batteryVoltage,
        BatteryPercent: batteryPercent,
        PsuTsFault: psuTsFault,
        PsuTsFaultString: psuTsFaultString,
        BatteryStatus: batteryStatus,
        BatteryStatusString: batteryStatusString,
        MeasurementMode: measurementMode,
        MeasurementModeString: measurementModeString,
        TempSen: tempSen,
        TempMBO: tempMbo,
        HeartRate: heartRate,
      } = await B1.getDeviceStatus()

      const deviceStatus = {
        batteryState,
        batteryStateString,
        deviceMode,
        deviceModeString,
        timestamp,
        logsToRead,
        state,
        stateString,
        batteryVoltage,
        batteryPercent,
        psuTsFault,
        psuTsFaultString,
        batteryStatus,
        batteryStatusString,
        measurementMode,
        measurementModeString,
        tempSen,
        tempMbo,
        heartRate,
      } satisfies DeviceStatus

      this.analyticsService.trackEvent(EVENT.BAND_DEVICE_STATUS, {
        deviceStatus,
        deviceStatusString: JSON.stringify(deviceStatus),
        name,
      })

      return deviceStatus
    } catch (err) {
      this.logError('getDeviceStatus', err)
      return undefined
    }
  }

  public async getErrorStatus(name?: string): Promise<ErrorStatus | undefined> {
    try {
      const {
        PMICConfigError: pmicConfigError,
        TempCommError: tempCommError,
        PPGCommError: ppgCommError,
      } = await B1.getErrorStatus()

      const errorStatus = {
        pmicConfigError,
        tempCommError,
        ppgCommError,
      } satisfies ErrorStatus

      this.analyticsService.trackEvent(EVENT.BAND_ERROR_STATUS, {
        errorStatus,
        errorStatusString: JSON.stringify(errorStatus),
        name,
      })

      return errorStatus
    } catch (err) {
      this.logError('getErrorStatus', err)
      return undefined
    }
  }

  private async getRSSI(): Promise<number | undefined> {
    try {
      const { RSSI: rssi } = await B1.getRSSI()
      return rssi
    } catch (err) {
      this.logError('getRSSI', err)
      return undefined
    }
  }

  private async getDeviceName(): Promise<string | undefined> {
    try {
      const { Name: name } = await B1.getDeviceName()
      return name
    } catch (err) {
      this.logError('getDeviceName', err)
      return undefined
    }
  }

  /**
   * Apparently, we call this a device name
   */
  public async getSerialNumber(): Promise<string | undefined> {
    try {
      const { Serial: serial } = await B1.getSerialNumber()
      return serial
    } catch (err) {
      this.logError('getSerialNumber', err)
      return undefined
    }
  }

  public async sendCommand(command: B1Command, data: number[]): Promise<void> {
    try {
      await B1.sendCommand({ Command: command, Data: data })
    } catch (err) {
      this.logError('sendCommand', err)
      return
    }

    this.analyticsService.trackEvent(EVENT.BAND_COMMAND_SENT, {
      command,
      description: B1Command[command],
      data,
      dataString: JSON.stringify(data),
    })
  }

  private async performFOTA(binaryFileName: string): Promise<void> {
    try {
      await B1.performFOTA({ binaryFileName })
    } catch (err) {
      this.logError('performFOTA', err)
    }
  }

  private async getManufacturerName(): Promise<string | undefined> {
    try {
      const { Manufacturer: manufacturer } = await B1.getManufacturerName()
      return manufacturer
    } catch (err) {
      this.logError('getManufacturerName', err)
      return undefined
    }
  }

  private async getModelNumber(): Promise<string | undefined> {
    try {
      const { Model: model } = await B1.getModelNumber()
      return model
    } catch (err) {
      this.logError('getModelNumber', err)
      return undefined
    }
  }

  private async getHardwareRevision(): Promise<string | undefined> {
    try {
      const { Version: version } = await B1.getHardwareRevision()
      return version
    } catch (err) {
      this.logError('getHardwareRevision', err)
      return undefined
    }
  }

  public async getSoftwareRevision(): Promise<string | undefined> {
    try {
      const { Version: version } = await B1.getSoftwareRevision()
      return version
    } catch (err) {
      this.logError('getSoftwareRevision', err)
      return undefined
    }
  }

  // TODO: add correct type with plugin updates
  public async getDiagnostics(name?: string): Promise<unknown | undefined> {
    try {
      const diagnostics = await B1.getDiagnostics()

      this.analyticsService.trackEvent(EVENT.BAND_DIAGNOSTICS, {
        diagnostics,
        diagnosticsString: JSON.stringify(diagnostics),
        name,
      })

      return diagnostics
    } catch (err) {
      this.logError('getDiagnostics', err)
      return undefined
    }
  }

  private async getRawDebugLog(): Promise<string | undefined> {
    try {
      const { b64LogData } = await B1.getRawDebugLog()
      return b64LogData
    } catch (err) {
      this.logError('getRawDebugLog', err)
      return undefined
    }
  }

  private async getLogsIndexes(): Promise<string[] | undefined> {
    try {
      const { Indexes } = await B1.getLogsIndexes()
      return Indexes
    } catch (err) {
      this.logError('getLogsIndexes', err)
      return undefined
    }
  }

  private async getLog(index: string, voltage = true): Promise<SleepLogData | undefined> {
    try {
      const { LogContentJSON: logContentJson } = await B1.getLog({ Index: index, Voltage: voltage })

      const sleepData = logContentJson ? (JSON.parse(logContentJson) as SleepLogData) : undefined
      return sleepData
    } catch (err) {
      this.logError('getLog', err)
      this.analyticsService.trackEvent(EVENT.BAND_READ_DATA_FAILED, {
        error: err,
        mode: 'single',
      })

      return undefined
    }
  }

  public async getNewLogs(voltage = true): Promise<SleepLogData[] | undefined> {
    try {
      const { LogContentJSON: logContentJson } = await B1.getNewLogs({ Voltage: voltage })

      const sleepData = logContentJson ? (JSON.parse(logContentJson) as SleepLogData[]) : undefined
      return sleepData
    } catch (err) {
      this.logError('getNewLogs', err)
      this.analyticsService.trackEvent(EVENT.BAND_READ_DATA_FAILED, {
        error: err,
        mode: 'new',
      })

      return undefined
    }
  }

  public async getAllLogs(voltage = true): Promise<SleepLogData[] | undefined> {
    try {
      const { LogContentJSON: logContentJson } = await B1.getAllLogs({ Voltage: voltage })

      const sleepData = logContentJson ? (JSON.parse(logContentJson) as SleepLogData[]) : undefined
      return sleepData
    } catch (err) {
      this.logError('getAllLogs', err)
      this.analyticsService.trackEvent(EVENT.BAND_READ_DATA_FAILED, {
        error: err,
        mode: 'all',
      })

      return undefined
    }
  }

  /**
   * Make sure to call this method only after you have confirmed that there were no errors while reading data!
   */
  public async ackLogs(): Promise<boolean> {
    try {
      const { Success: success } = await B1.ackLogs()
      return success
    } catch (err) {
      this.logError('ackLogs', err)
      return false
    }
  }

  private logError(method: string, err: unknown): void {
    this.analyticsService.trackEvent(EVENT.BAND_PLUGIN_ERROR, {
      method,
      error: _anyToErrorObject(err),
    })
    logUtil.error(err)
  }
}
